import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../auth/authActions";
import { useNavigate, useLocation } from "react-router-dom";
import { notBlank } from "../util/ext";
import { useSnackAlert } from "../common/use-snackAlert";

import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import TotalsHeader from "../creator-application/common/TotalsHeader";
import { useAllModules } from "./contentProviderServices";
import ContentProvidersTable from "./ContentProvidersTable";
import RealTimeSearchBar from "../creator-application/common/RealTimeSearchBar";
import { equals, filter, includes, toUpper } from "ramda";
import ContentProviderDialog from "./ContentProviderDialog";
import ProviderEndpointDialog from "./ProviderEndpointDialog";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
      height: "calc(100vh - 200px)",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listContainer: {
      // height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const ContentProvidersBrowse = ({ results }) => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();
  const navigate = useNavigate();
  const location = useLocation();
  const { SnackAlert, showAlert } = useSnackAlert();
  const [currentContentProvider, setCurrentContentProvider] = useState(null);
  const [
    currentContentProviderEndpoint,
    setCurrentContentProviderEndpoint,
  ] = useState(null);
  const [contentProviders, setContentProviders] = useState([]);
  const [contentProviderList, setContentProviderList] = useState([]);

  const [modules, setModules] = useState([]);
  const allModules = useAllModules();
  useEffect(() => {
    if (allModules.isSuccess && notBlank(allModules.data)) {
      setModules(allModules.data);
    }
  }, [allModules.isSuccess, allModules.data]);

  useEffect(() => {
    if (results.isError && results.error.status === 401) {
      dispatch(logOut());
    }

    if (results.isSuccess) {
      setScrollVisible(true);
      setContentProviders(results.data.content);
      setContentProviderList(results.data.content);
    }
  }, [
    dispatch,
    auth,
    results.data,
    results.isError,
    results.error,
    results.isSuccess,
  ]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const [contentProviderDialogOpen, setContentProviderDialogOpen] = useState(
    false,
  );
  const handleNewContentProviderClicked = useCallback(() => {
    setContentProviderDialogOpen(true);
  }, []);

  const handleEditContentProviderClicked = useCallback(contentProvider => {
    setCurrentContentProvider(contentProvider);
    setContentProviderDialogOpen(true);
  }, []);

  const handleContentProviderDialogClose = useCallback(() => {
    setContentProviderDialogOpen(false);
    setCurrentContentProvider(null);
  }, []);

  const handleContentProviderDialogSave = useCallback(
    async contentProvider => {
      setContentProviderDialogOpen(false);
      setCurrentContentProvider(null);
      showAlert("success", [
        `Content Provider [${contentProvider.name}] Saved`,
      ]);

      await results.refetch();
    },
    [showAlert, results],
  );

  const [providerEndpointDialogOpen, setProviderEndpointDialogOpen] = useState(
    false,
  );
  const handleAddEndpointClick = useCallback(contentProvider => {
    setCurrentContentProvider(contentProvider);
    setCurrentContentProviderEndpoint(null);
    setProviderEndpointDialogOpen(true);
  }, []);

  const handleEditEndpointClick = useCallback(
    (contentProvider, contentProviderEndpointId) => {
      setCurrentContentProvider(contentProvider);
      setCurrentContentProviderEndpoint(contentProviderEndpointId);
      setProviderEndpointDialogOpen(true);
    },
    [],
  );

  const handleProviderEndpointDialogClose = useCallback(() => {
    setProviderEndpointDialogOpen(false);
    setCurrentContentProvider(null);
  }, []);

  const handleProviderEndpointDialogSave = useCallback(
    async endpoint => {
      setProviderEndpointDialogOpen(false);
      setCurrentContentProvider(null);
      setCurrentContentProviderEndpoint(null);
      showAlert("success", [
        `Content Provider Endpoint [${endpoint.name}] Saved`,
      ]);

      await results.refetch();
    },
    [showAlert, results],
  );

  const [page, setPage] = useState(0);
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const [query, setQuery] = useState(null);
  const handleUpdateFilter = useCallback(
    newQuery => {
      setQuery(newQuery);
      if (notBlank(newQuery)) {
        const list = filter(
          cp =>
            includes(toUpper(newQuery), toUpper(cp.name)) ||
            equals(toUpper(newQuery), toUpper(cp.id)),
          contentProviders,
        );
        setContentProviderList(list);
      } else {
        setContentProviderList(contentProviders);
      }

      //-- Reset back to first page.
      setPage(0);
    },
    [contentProviders],
  );

  return (
    <Container
      id="content-provider-browse-view"
      classes={{ root: classes.root }}
    >
      <SnackAlert />

      {contentProviderDialogOpen && (
        <ContentProviderDialog
          open={contentProviderDialogOpen}
          contentProvider={currentContentProvider}
          onClose={handleContentProviderDialogClose}
          onSave={handleContentProviderDialogSave}
        />
      )}

      {providerEndpointDialogOpen && (
        <ProviderEndpointDialog
          open={providerEndpointDialogOpen}
          contentProvider={currentContentProvider}
          contentProviderEndpointId={currentContentProviderEndpoint}
          modules={modules}
          onClose={handleProviderEndpointDialogClose}
          onSave={handleProviderEndpointDialogSave}
        />
      )}

      <div>
        <RealTimeSearchBar
          query={query}
          onQueryChange={handleUpdateFilter}
          placeHolder="Search Content Providers"
          backButton={
            notBlank(location.state) &&
            notBlank(location.state.previousLocation) && (
              <Button
                aria-label="Go back"
                onClick={() => navigate(location.state.previousLocation)}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back
              </Button>
            )
          }
        />

        <TotalsHeader
          countText="Content Provider"
          count={results.isSuccess ? contentProviders.length : 0}
          showCount={true}
          selectedActions={
            <Box>
              <Button
                variant={"text"}
                startIcon={<AddIcon />}
                onClick={handleNewContentProviderClicked}
              >
                New Content Provider
              </Button>
            </Box>
          }
        />

        {results.isLoading && <PanelLoadingIndicator />}
        {results.isSuccess && (
          <Container classes={{ root: classes.panel }}>
            {notBlank(contentProviderList) ? (
              <ContentProvidersTable
                contentProviderList={contentProviderList}
                modules={modules}
                page={page}
                onPageChange={handlePageChange}
                onScrollVisible={handleScrollVisible}
                onEditProviderClick={handleEditContentProviderClicked}
                onAddEndpointClick={handleAddEndpointClick}
                onEditEndpointClick={handleEditEndpointClick}
                onError={showAlert}
              />
            ) : (
              <Box
                height="calc(100vh - 400px)"
                padding={5}
                fontSize="1.6rem"
                fontStyle="italic"
              >
                No Content Providers found matching given criteria.
              </Box>
            )}
          </Container>
        )}
      </div>
    </Container>
  );
};

export default ContentProvidersBrowse;
